import { Button, Icon, Tag, type Column } from '@dayon/design-system';
import { initials, relative, scopeLabel, stamp } from './format.ts';
import { OUTCOME, OUTCOME_DETAIL, STAGE, STATUS, TWO_FACTOR } from './vocabulary.ts';
import type { AdminRow, AttemptRow } from './types.ts';

/**
 * The tables this module draws, and nothing else.
 *
 * Every one of these is fixed at module scope except the roster, which needs a
 * callback to reach the drawer and is therefore a factory. Columns declared
 * inside a component are rebuilt on every keystroke in the search box.
 */

/**
 * The roster columns.
 *
 * `onManage` is the only reason this is a factory; everything inside it is
 * fixed.
 */
export function adminColumns(onManage: (admin: AdminRow) => void): Column<AdminRow>[] {
  return [
    {
      key: 'admin',
      header: 'Admin',
      render: (a) => (
        <div className="row" style={{ flexWrap: 'nowrap', gap: 'var(--space-3)' }}>
          <span className="avatar" aria-hidden="true">
            {initials(a.display_name)}
          </span>
          <div style={{ minWidth: 0 }}>
            {a.display_name}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {a.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      // Accented for the super role, neutral otherwise — is_super is a column,
      // never a match on the string "Super Admin".
      render: (a) => <Tag tone={a.is_super ? 'accent' : 'neutral'}>{a.role}</Tag>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (a) => {
        const chip = STATUS[a.status];
        return <Tag tone={chip?.tone ?? 'neutral'}>{chip?.label ?? a.status.toLowerCase()}</Tag>;
      },
    },
    {
      key: 'twofa',
      header: '2FA',
      /* Three states, and the words carry the distinction rather than the tone:
         "None" is the row a security lead scans this table for, and "Pending
         setup" is somebody halfway through — which used to render as None and
         made a reset look like an admin who had never bothered. */
      render: (a) => {
        const chip = TWO_FACTOR[a.two_factor];
        return <Tag tone={chip?.tone ?? 'outline'}>{chip?.label ?? a.two_factor}</Tag>;
      },
    },
    {
      key: 'scope',
      header: 'Brand scope',
      secondary: true,
      render: (a) => <span className="text-muted">{scopeLabel(a.brands)}</span>,
    },
    {
      key: 'active',
      header: 'Last active',
      secondary: true,
      render: (a) =>
        a.last_sign_in_at ? (
          <span className="text-muted">{relative(a.last_sign_in_at)}</span>
        ) : (
          <span className="text-subtle">never</span>
        ),
    },
    {
      key: 'action',
      header: 'Action',
      numeric: true,
      render: (a) => (
        <Button small onClick={() => onManage(a)} aria-label={`Manage ${a.display_name}`}>
          Manage
        </Button>
      ),
    },
  ];
}

/** The audit tab: the sign-in trail, newest first. */
export const attemptColumns: Column<AttemptRow>[] = [
  {
    key: 'when',
    header: 'When',
    render: (a) => <span className="tabular">{stamp(a.created_at)}</span>,
  },
  {
    key: 'actor',
    header: 'Actor',
    render: (a) => (
      <>
        {a.display_name ?? <span className="text-subtle">unknown address</span>}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {a.email_attempted}
        </div>
      </>
    ),
  },
  {
    key: 'result',
    header: 'Result',
    render: (a) => {
      const chip = OUTCOME[a.outcome];
      return <Tag tone={chip?.tone ?? 'outline'}>{chip?.label ?? a.outcome.toLowerCase()}</Tag>;
    },
  },
  {
    key: 'detail',
    header: 'Detail',
    render: (a) => (
      <span className="text-muted">
        {STAGE[a.stage] ?? a.stage} · {OUTCOME_DETAIL[a.outcome] ?? a.outcome.toLowerCase()}
      </span>
    ),
  },
  {
    key: 'ip',
    header: 'IP',
    secondary: true,
    render: (a) => <span className="text-subtle tabular">{a.ip ?? '—'}</span>,
  },
];

/** The drawer's Login history table — the design's When / Result / Detail. */
export const historyColumns: Column<AttemptRow>[] = [
  {
    key: 'when',
    header: 'When',
    render: (a) => <span className="tabular">{stamp(a.created_at)}</span>,
  },
  {
    key: 'result',
    header: 'Result',
    render: (a) => {
      const chip = OUTCOME[a.outcome];
      return <Tag tone={chip?.tone ?? 'outline'}>{chip?.label ?? a.outcome.toLowerCase()}</Tag>;
    },
  },
  {
    key: 'detail',
    header: 'Detail',
    render: (a) => (
      <span className="text-muted">
        {STAGE[a.stage] ?? a.stage} · {a.ip ?? 'no IP recorded'}
      </span>
    ),
  },
];

/** A drawer section heading — "ACTIVE SESSIONS", "LOGIN HISTORY". */
export function SectionLabel({ children }: { children: string }) {
  return (
    <div className="text-subtle" style={{ fontSize: '11px', letterSpacing: '0.08em' }}>
      {children}
    </div>
  );
}

/**
 * One matrix cell, as a CONTROL.
 *
 * It used to be a glyph, because the grid was the ROLE_MATRIX constant and no
 * click could have changed what any guard enforced. The grid is
 * identity.role_permission now, so a cell is a button — and the three states it
 * can be in are three different things rather than two:
 *
 *   · granted / withheld — a real toggle, and pressing it opens a confirmation
 *     that takes a reason, because "every change is audit-logged" means every
 *     change needs something worth logging;
 *   · LOCKED — access.manage on a super role. The route asks refuseRoleChange
 *     and the platform's last-administrator rule; the console does not offer
 *     the cell at all, because "Super Admin cannot lose Manage roles & users"
 *     is not a decision an operator should have to be talked out of;
 *   · CLOSED — a write capability on a read-only role. The route refuses it, so
 *     the cell says so rather than opening a dialog that ends in a modal.
 *
 * Shape still carries the state, not hue: a tick, a dash, a padlock. The label
 * is read aloud in full because the column header alone ("Finance Lead") does
 * not say whether the glyph means granted or withheld — and now also says what
 * pressing it would do.
 */
export function GrantCell({
  granted,
  locked,
  closed,
  role,
  capability,
  onToggle,
}: {
  granted: boolean;
  locked: boolean;
  closed: string | null;
  role: string;
  capability: string;
  onToggle: (() => void) | null;
}) {
  if (locked) {
    return (
      <span
        title="Super Admin cannot lose Manage roles & users"
        style={{ cursor: 'not-allowed' }}
      >
        <Icon name="lock-simple" label={`${capability} is permanently granted to ${role}`} />
      </span>
    );
  }

  const glyph = granted ? 'check-circle' : 'minus';
  const state = granted ? `${role} holds ${capability}` : `${role} does not hold ${capability}`;

  if (closed !== null || onToggle === null) {
    return (
      <span title={closed ?? undefined} style={{ cursor: 'not-allowed', opacity: 0.75 }}>
        <Icon
          name={glyph}
          label={`${state}${closed ? ` — ${closed}` : ''}`}
          style={{ color: granted ? 'var(--portal-accent-text)' : 'var(--color-text-subtle)' }}
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      className="matrix-cell"
      onClick={onToggle}
      /* The whole sentence, because a screen-reader user meets this button with
         no column header in earshot: what the state IS, and what pressing it
         would do. */
      aria-label={`${state}. ${granted ? 'Take it away' : 'Grant it'}.`}
      aria-pressed={granted}
    >
      <Icon
        name={glyph}
        style={{ color: granted ? 'var(--portal-accent-text)' : 'var(--color-text-subtle)' }}
      />
    </button>
  );
}
